'use client'

import { useRef, useState, type FormEvent } from 'react'
import type { Route } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { ArrowRight, Loader2, UserRoundKey } from 'lucide-react'

import { AdminLoginFilledIcon } from '@/components/icons/filled'

import { SignInLoggedInNotification } from '../_components/sign-in-logged-in-notification'
import { SignInErrorNotification } from '../_components/sign-in-error-notification'
import { DEFAULT_AUTH_CALLBACK_URL, signInEmail } from '@/lib/auth-client'
import { cn } from '@/lib/utils'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const PRESS_SCALE_CLASS =
  'active:scale-[0.96] transition-[scale,background-color,color,border-color]'

function isSafeRedirect(url: string | undefined): url is string {
  return (
    !!url &&
    url.startsWith('/') &&
    !url.startsWith('//') &&
    !url.startsWith('/\\')
  )
}

interface SignInFormProps {
  callbackURL?: string
  isInviteSignIn: boolean
  lockedEmail?: string
  message?: { text: string }
  showSocial: boolean
  superuserEmails: readonly string[]
}

export function SignInForm({
  callbackURL,
  isInviteSignIn,
  lockedEmail,
  message,
  showSocial,
  superuserEmails,
}: SignInFormProps) {
  const router = useRouter()
  const passwordRef = useRef<HTMLInputElement>(null)
  const [isRedirectPending, setIsRedirectPending] = useState(false)
  const [email, setEmail] = useState(() => lockedEmail ?? '')
  const [password, setPassword] = useState('')
  const [isEmailLocked, setIsEmailLocked] = useState(() => Boolean(lockedEmail))
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [superuserSelectValue, setSuperuserSelectValue] = useState('')

  const showSuperuserSelect =
    showSocial && superuserEmails.length > 0 && !isEmailLocked

  const redirectTo = isSafeRedirect(callbackURL)
    ? callbackURL
    : DEFAULT_AUTH_CALLBACK_URL

  const adminSignInHref = isSafeRedirect(callbackURL)
    ? `/sign-in-admin?${new URLSearchParams({ callbackURL }).toString()}`
    : '/sign-in-admin'

  function handleUnlockEmail(): void {
    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.delete('email')

    setEmail('')
    setSuperuserSelectValue('')
    setIsEmailLocked(false)
    router.replace(`${currentUrl.pathname}${currentUrl.search}` as Route)
  }

  function handleSuperuserSelect(value: string): void {
    setSuperuserSelectValue(value)
    setEmail(value)
    passwordRef.current?.focus()
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!email || !password) return

    setIsLoading(true)
    setError('')
    setSuccess('')

    try {
      const { error: signInError } = await signInEmail(
        email,
        password,
        redirectTo,
      )

      if (signInError) {
        setError(signInError.message || 'Invalid email or password')
        setIsLoading(false)
        return
      }

      setSuccess('Signed in successfully.')
      setIsRedirectPending(true)
      router.push(redirectTo as Route)
    } catch {
      setError('An unexpected error occurred')
      setIsLoading(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-md min-w-64 space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-balance">
          {isInviteSignIn
            ? 'Sign in to accept your invitation'
            : 'Welcome back'}
        </h1>
        <p className="text-muted-foreground text-sm text-pretty">
          {isInviteSignIn
            ? `Use the account for ${lockedEmail ?? email} to join.`
            : 'Please sign in to your account'}
        </p>
      </div>

      {showSuperuserSelect ? (
        <div className="space-y-2">
          <Label htmlFor="superuser-select" className="text-sm font-medium">
            Quick sign-in (superuser)
          </Label>
          <Select
            value={superuserSelectValue}
            onValueChange={handleSuperuserSelect}
          >
            <SelectTrigger
              id="superuser-select"
              className="corner-squircle h-10 w-full rounded-lg"
            >
              <SelectValue placeholder="Select a superuser account" />
            </SelectTrigger>
            <SelectContent>
              {superuserEmails.map((superuserEmail) => (
                <SelectItem key={superuserEmail} value={superuserEmail}>
                  {superuserEmail}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="w-full space-y-4">
        <div className="grid w-full grid-cols-[1fr_auto] gap-x-2 gap-y-2">
          <Label
            htmlFor="email"
            className="col-start-1 row-start-1 self-end text-sm font-medium"
          >
            Email address
          </Label>
          {isEmailLocked && !isInviteSignIn ? (
            <Button
              className="col-start-2 row-start-1 h-auto self-end px-0 text-xs"
              disabled={isLoading || isRedirectPending}
              onClick={handleUnlockEmail}
              type="button"
              variant="link"
            >
              Use different email
            </Button>
          ) : (
            <span
              aria-hidden
              className="invisible col-start-2 row-start-1 self-end text-xs"
            >
              Forgot your password?
            </span>
          )}
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="name@example.com"
            value={email}
            onChange={(event) => {
              const next = event.target.value
              setEmail(next)
              if (next !== superuserSelectValue) setSuperuserSelectValue('')
            }}
            readOnly={isEmailLocked}
            required
            className={`corner-squircle col-span-2 row-start-2 h-10 w-full rounded-lg ${
              isEmailLocked
                ? 'bg-muted/60 text-muted-foreground cursor-not-allowed'
                : ''
            }`}
            disabled={isLoading || isRedirectPending}
          />
          {isEmailLocked ? (
            <p className="text-muted-foreground col-span-2 row-start-3 text-xs leading-relaxed">
              This invite can only be accepted by {email}.
            </p>
          ) : null}
        </div>

        <div className="grid w-full grid-cols-[1fr_auto] gap-x-2 gap-y-2">
          <Label
            htmlFor="password"
            className="col-start-1 row-start-1 self-end text-sm font-medium"
          >
            Password
          </Label>
          <Link
            href={
              (email
                ? `/forgot-password?${new URLSearchParams({ email }).toString()}`
                : '/forgot-password') as Route
            }
            className="text-muted-foreground hover:text-primary col-start-2 row-start-1 -my-2 self-end py-2 text-xs underline-offset-4 hover:underline"
          >
            Forgot your password?
          </Link>
          <Input
            ref={passwordRef}
            id="password"
            name="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            className="corner-squircle col-span-2 row-start-2 h-10 w-full rounded-lg"
            disabled={isLoading || isRedirectPending}
          />
        </div>

        <Button
          type="submit"
          className={cn(
            'corner-squircle h-10 w-full rounded-lg',
            PRESS_SCALE_CLASS,
          )}
          disabled={isLoading || isRedirectPending || !email || !password}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in...
            </>
          ) : (
            <>
              <UserRoundKey className="mr-2 h-4 w-4" />
              Sign in
            </>
          )}
        </Button>
      </form>

      {success ? (
        <SignInLoggedInNotification
          message={success}
          isPending={isRedirectPending}
        />
      ) : null}

      {error ? <SignInErrorNotification message={error} /> : null}

      {message?.text && !success && !error ? (
        <Alert>
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      ) : null}

      {showSocial && !isInviteSignIn ? (
        <div className="space-y-3">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="border-border/50 w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card text-muted-foreground px-2">
                Platform admin
              </span>
            </div>
          </div>

          <p className="text-muted-foreground text-center text-xs text-pretty">
            Sign in with GitHub, Google, or Microsoft on the admin console.
          </p>

          <Button
            render={<Link href={adminSignInHref as Route} />}
            nativeButton={false}
            variant="outline"
            className="corner-squircle h-10 w-full rounded-lg"
          >
            <AdminLoginFilledIcon />
            Admin login
            <ArrowRight
              className="text-muted-foreground size-4 transition-colors duration-150 group-hover/button:text-primary"
              aria-hidden
            />
          </Button>
        </div>
      ) : null}
    </div>
  )
}
